import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, DetailItem, formatNumber, getApiErrorMessage, inputClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { clean, openButtonClass, TableFooter, thClass, useListState } from '../quality/partApprovalShared';

type Action = 'create' | 'update' | 'delete' | 'action' | 'export' | 'login' | 'login_failed' | 'logout';

interface RecordChange {
  entity: string;
  action: 'create' | 'update' | 'delete';
  id: string | null;
  label: string | null;
  fields?: Record<string, { from?: unknown; to: unknown }>;
}

interface AuditEntry {
  id: string;
  createdAt: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  action: Action;
  step: string | null;
  area: string;
  areaLabel: string;
  summary: string;
  route: string;
  method: string;
  path: string;
  recordId: string | null;
  recordLabel: string | null;
  statusCode: number;
  payload: Record<string, unknown> | null;
  changes: RecordChange[];
  changesDropped: number;
  ipAddress: string | null;
  userAgent: string | null;
}

interface AuditPage {
  data: AuditEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface AuditSummary {
  today: number;
  usersToday: number;
  deletionsLast7Days: number;
  failedSignInsLast7Days: number;
}

interface AuditFilters {
  users: { id: string; name: string; email: string | null }[];
  areas: { key: string; label: string }[];
  actions: { key: Action; label: string }[];
}

const ACTION: Record<Action, { label: string; tone: string }> = {
  create: { label: 'Added', tone: 'bg-green-50 text-green-700 ring-green-600/20' },
  update: { label: 'Changed', tone: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  delete: { label: 'Deleted', tone: 'bg-red-50 text-red-700 ring-red-600/20' },
  action: { label: 'Step', tone: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20' },
  export: { label: 'Exported', tone: 'bg-amber-50 text-amber-700 ring-amber-600/20' },
  login: { label: 'Signed in', tone: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  login_failed: { label: 'Sign-in failed', tone: 'bg-red-50 text-red-700 ring-red-600/20' },
  logout: { label: 'Signed out', tone: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
};

const CHANGE_WORD: Record<RecordChange['action'], string> = { create: 'Added', update: 'Changed', delete: 'Deleted' };

const formatMoment = (value: string) =>
  new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

// "purchase_orders" -> "Purchase orders", "costPrice" -> "Cost price"
const words = (value: string) => {
  const text = value.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const shown = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const who = (entry: AuditEntry) => entry.userName || entry.userEmail || 'Nobody signed in';

const EntryPanel: React.FC<{ entryId: string; onClose: () => void; onRecord: (recordId: string) => void }> = ({ entryId, onClose, onRecord }) => {
  const { data: entry, isLoading, error } = useQuery<AuditEntry>({
    queryKey: ['audit-log', entryId],
    queryFn: async () => (await api.get(`/audit-logs/${entryId}`)).data,
  });

  if (isLoading) return <p className="py-8 text-center text-sm text-gray-500">Loading…</p>;
  if (error || !entry) return <p className="py-8 text-center text-sm text-red-600">{getApiErrorMessage(error, 'This entry could not be loaded')}</p>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn(badgeClass, 'ring-1 ring-inset', ACTION[entry.action].tone)}>{ACTION[entry.action].label}</span>
        <span className="text-sm font-medium text-gray-900">{entry.summary}</span>
      </div>

      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <DetailItem label="When">{formatMoment(entry.createdAt)}</DetailItem>
        <DetailItem label="Who">{who(entry)}{entry.userName && entry.userEmail ? <span className="block text-xs text-gray-500">{entry.userEmail}</span> : null}</DetailItem>
        <DetailItem label="Area">{entry.areaLabel}</DetailItem>
        <DetailItem label="Record">{entry.recordLabel || entry.recordId || '—'}</DetailItem>
        <DetailItem label="From address">{entry.ipAddress || '—'}</DetailItem>
        <DetailItem label="Request">{entry.method} {entry.path}<span className="block text-xs text-gray-500">Answered with {entry.statusCode}</span></DetailItem>
      </dl>
      {entry.userAgent && <p className="break-all text-xs text-gray-500">Browser or program: {entry.userAgent}</p>}

      <div>
        <h4 className="text-sm font-semibold text-gray-900">Records it wrote</h4>
        {entry.changes.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">
            {entry.action === 'login' || entry.action === 'login_failed' || entry.action === 'logout' || entry.action === 'export'
              ? 'Nothing was changed.'
              : 'No record was listed for this action.'}
          </p>
        ) : (
          <div className="mt-2 space-y-3">
            {entry.changes.map((change, index) => (
              <div key={`${change.entity}-${change.id}-${index}`} className="rounded-md ring-1 ring-gray-200" data-testid="audit-change">
                <div className="flex flex-wrap items-center gap-2 bg-gray-50 px-3 py-2 text-sm">
                  <span className="font-medium text-gray-900">{CHANGE_WORD[change.action]}</span>
                  <span className="text-gray-700">{words(change.entity)}</span>
                  {change.label && <span className="text-gray-900">{change.label}</span>}
                </div>
                {change.fields && Object.keys(change.fields).length > 0 && (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Field</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Before</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">After</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {Object.entries(change.fields).map(([field, value]) => (
                        <tr key={field}>
                          <td className="px-3 py-2 text-gray-700">{words(field)}</td>
                          <td className="break-all px-3 py-2 text-gray-500">{'from' in value ? shown(value.from) : 'Not recorded'}</td>
                          <td className="break-all px-3 py-2 text-gray-900">{shown(value.to)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
            {entry.changesDropped > 0 && <p className="text-xs text-gray-500">and {formatNumber(entry.changesDropped)} more records that are not listed.</p>}
          </div>
        )}
      </div>

      {entry.payload && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900">What was sent</h4>
          <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-gray-50 p-3 text-xs text-gray-800 ring-1 ring-gray-200" data-testid="audit-payload">{JSON.stringify(entry.payload, null, 2)}</pre>
        </div>
      )}

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        {entry.recordId && entry.action !== 'export' && (
          <button type="button" onClick={() => onRecord(entry.recordId as string)} className={secondaryButtonClass}>Everything on this record</button>
        )}
        <button type="button" onClick={onClose} className={secondaryButtonClass}>Close</button>
      </div>
    </div>
  );
};

const AuditLog: React.FC = () => {
  const { page, setPage, limit, setLimit, searchInput, setSearchInput, search } = useListState();
  const [userId, setUserId] = useState('');
  const [area, setArea] = useState('');
  const [action, setAction] = useState<Action | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [recordId, setRecordId] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);

  const change = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(1);
  };

  const { data, isLoading, error } = useQuery<AuditPage>({
    queryKey: ['audit-logs', page, limit, search, userId, area, action, from, to, recordId],
    queryFn: async () => (await api.get('/audit-logs', { params: clean({ page, limit, search, userId, area, action, from, to, recordId }) })).data,
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery<AuditSummary>({ queryKey: ['audit-logs', 'summary'], queryFn: async () => (await api.get('/audit-logs/summary')).data });
  const { data: filters } = useQuery<AuditFilters>({ queryKey: ['audit-logs', 'filters'], queryFn: async () => (await api.get('/audit-logs/filters')).data });

  const rows = data?.data ?? [];
  const filtered = !!(search || userId || area || action || from || to || recordId);
  const datesWrong = !!from && !!to && from > to;

  const clear = () => {
    setSearchInput('');
    setUserId('');
    setArea('');
    setAction('');
    setFrom('');
    setTo('');
    setRecordId('');
    setPage(1);
  };

  const stats = [
    { label: 'Entries today', value: formatNumber(summary?.today ?? 0), hint: 'Everything added, changed, deleted, exported and every sign-in' },
    { label: 'Users active today', value: formatNumber(summary?.usersToday ?? 0), hint: 'People with at least one entry today' },
    { label: 'Deletions, last 7 days', value: formatNumber(summary?.deletionsLast7Days ?? 0), hint: 'Records that were deleted' },
    { label: 'Failed sign-ins, last 7 days', value: formatNumber(summary?.failedSignInsLast7Days ?? 0), hint: 'Wrong password, unknown or locked account', alert: (summary?.failedSignInsLast7Days ?? 0) > 0 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Audit Trail</h1>
        <p className="mt-1 text-sm text-gray-500">Who added, changed and deleted what, and who signed in. The system writes this itself; entries cannot be edited or removed here.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-red-600' : 'text-gray-900')}>{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="space-y-3 border-b border-gray-200 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by record number or name, user e-mail, or address" aria-label="Search the audit trail" className={cn(inputClass, 'pl-9')} />
            </div>
            <select value={userId} onChange={(event) => change(setUserId)(event.target.value)} aria-label="User" className={cn(inputClass, 'lg:w-52')}>
              <option value="">All users</option>
              {(filters?.users ?? []).map((user) => (<option key={user.id} value={user.id}>{user.name}</option>))}
            </select>
            <select value={area} onChange={(event) => change(setArea)(event.target.value)} aria-label="Area" className={cn(inputClass, 'lg:w-48')}>
              <option value="">All areas</option>
              {(filters?.areas ?? []).map((item) => (<option key={item.key} value={item.key}>{item.label}</option>))}
            </select>
            <select value={action} onChange={(event) => change(setAction)(event.target.value as Action | '')} aria-label="Kind of entry" className={cn(inputClass, 'lg:w-44')}>
              <option value="">All kinds</option>
              {(Object.keys(ACTION) as Action[]).map((key) => (<option key={key} value={key}>{ACTION[key].label}</option>))}
            </select>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              From
              <input type="date" value={from} max={to || undefined} onChange={(event) => change(setFrom)(event.target.value)} aria-label="From date" className={cn(inputClass, 'w-44')} />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              To
              <input type="date" value={to} min={from || undefined} onChange={(event) => change(setTo)(event.target.value)} aria-label="To date" className={cn(inputClass, 'w-44')} />
            </label>
            {datesWrong && <span className="text-sm text-red-600">The first day is after the last day.</span>}
            {recordId && (
              <span className={cn(badgeClass, 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20')} data-testid="audit-record-filter">
                One record only
                <button type="button" onClick={() => change(setRecordId)('')} aria-label="Stop showing one record only" className="ml-1.5 font-semibold hover:text-indigo-900">×</button>
              </span>
            )}
            {filtered && <button type="button" onClick={clear} className="text-sm font-medium text-indigo-600 hover:text-indigo-800 sm:ml-auto">Clear filters</button>}
          </div>
        </div>

        {error ? (
          <p className="px-4 py-10 text-center text-sm text-red-600">{getApiErrorMessage(error, 'The audit trail could not be loaded')}</p>
        ) : isLoading ? (
          <p className="px-4 py-10 text-center text-sm text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm font-medium text-gray-900">{filtered ? 'No entries match these filters' : 'Nothing has been recorded yet'}</p>
            <p className="mt-1 text-sm text-gray-500">{filtered ? 'Try a wider date range or clear the filters.' : 'Entries appear here as people use the system.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={thClass}>When</th>
                  <th className={thClass}>Who</th>
                  <th className={thClass}>What</th>
                  <th className={thClass}>Area</th>
                  <th className={thClass}>Record</th>
                  <th className={cn(thClass, 'text-right')}><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {rows.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50" data-testid="audit-row">
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">{formatMoment(entry.createdAt)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="font-medium text-gray-900">{who(entry)}</span>
                      {entry.ipAddress && <span className="block text-xs text-gray-500">{entry.ipAddress}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={cn(badgeClass, 'mr-2 ring-1 ring-inset', ACTION[entry.action].tone)}>{ACTION[entry.action].label}</span>
                      <span className="text-gray-700">{entry.step ? words(entry.step.replace(/[/-]/g, '_')) : ''}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{entry.areaLabel}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {entry.recordLabel || (entry.action === 'login_failed' ? entry.userEmail : null) || <span className="text-gray-400">—</span>}
                      {entry.changes.length > 1 && <span className="block text-xs text-gray-500">{formatNumber(entry.changes.length + entry.changesDropped)} records written</span>}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" onClick={() => setViewingId(entry.id)} className={openButtonClass}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && rows.length > 0 && <TableFooter page={data.page} limit={limit} total={data.total} totalPages={data.totalPages} onPage={setPage} onLimit={setLimit} />}
      </div>

      <Modal isOpen={!!viewingId} onClose={() => setViewingId(null)} title="Audit Entry" size="xl">
        {viewingId && (
          <EntryPanel
            key={viewingId}
            entryId={viewingId}
            onClose={() => setViewingId(null)}
            onRecord={(id) => {
              clear();
              setRecordId(id);
              setViewingId(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default AuditLog;
